import React, { useCallback } from "react";
import { theme } from "antd";
import { useNavigation, useTranslate } from "@refinedev/core";
import styled from "styled-components";
import { CaretDownIcon, PlusIcon } from "@phosphor-icons/react";
import { Divider, Grid, Input, TextArea } from "@libertify/ui";
import type { SelectProps } from "@libertify/ui";
import { LanguageSelect } from "components/LanguageSelect";
import { PlaybookLock } from "../../PlaybookLock";
import { PlaybookFormItem } from "../../PlaybookFormItem";
import { PlaybookSelect } from "../../PlaybookSelect";
import { DashedAddButton } from "../../DashedAddButton";
import { BrandKitOptionRow } from "../BrandKitOptionRow";
import { useGeneralConfigData } from "../hooks/useGeneralConfigData";
import { usePlaybookEditMode } from "../../../context/PlaybookEditModeContext";
import {
  ChipsSelect,
  SectionHeading,
  TagsSelect,
} from "./GeneralSettingsConfigCard.styles";

const KEY = "createExperience.generalSettings";
// The generation fields kept their labels when they moved here from the video
// card, and with them their original locale keys.
const VIDEO_KEY = "createExperience.videoConfig";

/** The section's continuously-typed fields — the ones the edit card commits on
 *  blur rather than per keystroke. */
export type ExperienceDetailsBlurField =
  | "name"
  | "description"
  | "generation_prompt";

/** Generation fields a host card can leave out of the section. */
export type ExperienceDetailsHiddenField =
  | "generation_prompt"
  | "objectives"
  | "language";

interface ExperienceDetailsSectionProps {
  /**
   * Fired when a text input loses focus — the edit card uses this to commit
   * text edits without a Save button. No-op if omitted (the create card
   * doesn't persist until the whole draft submits).
   */
  onFieldBlur?: (field: ExperienceDetailsBlurField) => void;
  /** Offer "Auto" (detect from the document) as a language. */
  includeAutoLanguage?: boolean;
  /**
   * Fields to leave unrendered. Their values stay in the form store, so the
   * host card can keep sending them unchanged. Default: show everything.
   */
  hiddenFields?: readonly ExperienceDetailsHiddenField[];
}

/**
 * "Experience details" fields — the experience identity (name/language/tags/
 * description) plus the experience-wide generation fields (goal, voice,
 * prompt, brand kit — the backend `GeneralGenerationSettings`). Shared between
 * the create-flow config card and the edit card so both render the exact same
 * fields. Must be rendered inside a Form bound to `GeneralSettingsFormValues`
 * (or a superset of it).
 */
export const ExperienceDetailsSection: React.FC<
  ExperienceDetailsSectionProps
> = ({ onFieldBlur, includeAutoLanguage = false, hiddenFields = [] }) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { createUrl } = useNavigation();
  // Opened in a new tab so the user keeps their unsaved edits.
  const brandKitCreateUrl = createUrl("media_brandkits");
  const { goals, brandKits, voices, loading } = useGeneralConfigData();
  const isEditingPlaybook = usePlaybookEditMode();

  const renderBrandKitOption: NonNullable<SelectProps["optionRender"]> =
    useCallback(
      (opt) => {
        const kit = brandKits.find((b) => b.value === opt.value);
        return kit ? <BrandKitOptionRow option={kit} /> : opt.label;
      },
      [brandKits]
    );

  const renderBrandKitLabel: NonNullable<SelectProps["labelRender"]> =
    useCallback(
      (props) => {
        const kit = brandKits.find((b) => b.value === props.value);
        return kit ? <BrandKitOptionRow option={kit} /> : props.label;
      },
      [brandKits]
    );

  const renderBrandKitPopup: NonNullable<SelectProps["popupRender"]> =
    useCallback(
      (menu) => (
        <>
          {menu}
          <Divider spacing="sm" />
          <DashedAddButton
            icon={<PlusIcon size={16} />}
            label={t("pages.playbooks.createBrandkit")}
            iconBordered={false}
            centered
            onClick={() =>
              globalThis.open(
                brandKitCreateUrl,
                "_blank",
                "noopener,noreferrer"
              )
            }
          />
        </>
      ),
      [t, brandKitCreateUrl]
    );

  const placeholder = t(`${VIDEO_KEY}.selectPlaceholder`);
  const showLanguage = !hiddenFields.includes("language");
  const showObjectives = !hiddenFields.includes("objectives");
  const showPrompt = !hiddenFields.includes("generation_prompt");

  // Split out so playbook-edit mode can group the experience-only three under
  // one `PlaybookLock` without disturbing the create flow's field order.
  const nameField = (
    <PlaybookFormItem
      span={2}
      name="name"
      labelKey={`${KEY}.details.name.label`}
    >
      <Input
        size="l"
        placeholder={t(`${KEY}.details.name.placeholder`)}
        onBlur={() => onFieldBlur?.("name")}
      />
    </PlaybookFormItem>
  );

  const tagsField = (
    <PlaybookFormItem
      // Full width when Language, its row partner, is hidden.
      span={isEditingPlaybook || !showLanguage ? 2 : undefined}
      name="tags"
      labelKey={`${KEY}.details.tags.label`}
    >
      <TagsSelect
        $token={token}
        $placeholder={t(`${KEY}.details.tags.placeholder`)}
        size="l"
        mode="tags"
        open={false}
        suffixIcon={null}
      />
    </PlaybookFormItem>
  );

  const descriptionField = (
    <PlaybookFormItem
      span={2}
      name="description"
      labelKey={`${KEY}.details.description.label`}
    >
      <TextArea
        rows={3}
        placeholder={t(`${KEY}.details.description.placeholder`)}
        onBlur={() => onFieldBlur?.("description")}
      />
    </PlaybookFormItem>
  );

  const languageField = showLanguage ? (
    <PlaybookFormItem
      // API `GeneralGenerationSettings.language` — nested so the branch can
      // be sent to the General add-on settings untranslated.
      name={["generation_settings", "language"]}
      labelKey={`${KEY}.details.language.label`}
    >
      <LanguageSelect
        size="large"
        showSearch
        optionFilterProp="label"
        includeAuto={includeAutoLanguage}
      />
    </PlaybookFormItem>
  ) : null;

  const generationFields = (
    <>
      {showObjectives ? (
        <PlaybookFormItem
          // API `objectives` is an `Objective[]` — bound as-is to a
          // multi-select. Removing every chip leaves `[]` (not undefined) so
          // the edit card still sees the change and commits the clear.
          name={["generation_settings", "objectives"]}
          labelKey={`${KEY}.details.objectives.label`}
        >
          <ChipsSelect
            $token={token}
            size="l"
            mode="multiple"
            options={goals}
            optionFilterProp="label"
            placeholder={placeholder}
            suffixIcon={<CaretDownIcon weight="bold" />}
          />
        </PlaybookFormItem>
      ) : null}

      <PlaybookFormItem
        // The API field is the contact — the backend derives the voice from
        // it (see useGeneralConfigData.voices).
        name={["generation_settings", "contact_id"]}
        labelKey={`${KEY}.details.voice.label`}
      >
        <PlaybookSelect
          options={voices}
          placeholder={placeholder}
          loading={loading.voices}
        />
      </PlaybookFormItem>

      {showPrompt ? (
        <PlaybookFormItem
          span={2}
          name={["generation_settings", "generation_prompt"]}
          labelKey={`${VIDEO_KEY}.prompt.label`}
        >
          <TextArea
            rows={3}
            placeholder={t(`${VIDEO_KEY}.prompt.placeholder`)}
            onBlur={() => onFieldBlur?.("generation_prompt")}
          />
        </PlaybookFormItem>
      ) : null}

      <PlaybookFormItem
        // Half width beside Voice when Goal and Prompt are both hidden.
        span={!showObjectives && !showPrompt ? undefined : 2}
        name={["generation_settings", "brand_kit_id"]}
        labelKey={`${VIDEO_KEY}.brandKit.label`}
        // Clearing stores an explicit `null` (not undefined): it must beat
        // the org-default seed in the draft merge and reach the API as
        // "no kit — generate the look & feel from the document instead".
        normalize={(value) => value ?? null}
      >
        <BrandKitSelect
          allowClear
          options={brandKits}
          placeholder={placeholder}
          loading={loading.brandKits}
          optionRender={renderBrandKitOption}
          labelRender={renderBrandKitLabel}
          popupRender={renderBrandKitPopup}
        />
      </PlaybookFormItem>
    </>
  );

  return (
    <>
      <SectionHeading>{t(`${KEY}.details.heading`)}</SectionHeading>
      {isEditingPlaybook ? (
        <>
          {/* Title, tags and description name the EXPERIENCE; no playbook
              field holds them. */}
          <PlaybookLock variant="fields">
            <Grid columns={2} gap="lg">
              {nameField}
              {tagsField}
              {descriptionField}
            </Grid>
          </PlaybookLock>
          <Grid columns={2} gap="lg">
            {languageField}
            {generationFields}
          </Grid>
        </>
      ) : (
        <Grid columns={2} gap="lg">
          {nameField}
          {languageField}
          {tagsField}
          {descriptionField}
          {generationFields}
        </Grid>
      )}
    </>
  );
};

export default ExperienceDetailsSection;

// ─── Styled components ───

// The brand kit row (logo + name + colors + font preview) is taller than a
// plain text option, so the closed control needs to grow with it instead of
// clipping at the Select's fixed height.
const BrandKitSelect = styled(PlaybookSelect)`
  .ant-select-selector {
    height: auto !important;
    min-height: 40px;
    padding-top: 6px !important;
    padding-bottom: 6px !important;
  }

  .ant-select-selection-item {
    line-height: normal !important;
  }
`;
